// src/pages/CohortLectureRoomPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cohortService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, ArrowLeft, Video, Users, Clock } from 'lucide-react';

const CohortLectureRoomPage = () => {
  const { id: cohortId, lectureId } = useParams();
  const { user } = useAuth();
  const [lecture, setLecture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);

  const attendanceIdRef = useRef(null);
  const heartbeatTimerRef = useRef(null);

  useEffect(() => {
    cohortService.getLectureById(cohortId, lectureId)
      .then((res) => {
        const l = res.data?.data?.lecture ?? res.data?.lecture;
        setLecture(l);
      })
      .catch((err) => setError(err.response?.data?.message || 'Could not load lecture room'))
      .finally(() => setLoading(false));
  }, [cohortId, lectureId]);

  // Clean up attendance timers on unmount or tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (attendanceIdRef.current) {
        cohortService.leaveLecture(cohortId, lectureId, { attendanceId: attendanceIdRef.current }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (attendanceIdRef.current) {
        cohortService.leaveLecture(cohortId, lectureId, { attendanceId: attendanceIdRef.current }).catch(() => {});
        attendanceIdRef.current = null;
      }
    };
  }, [cohortId, lectureId]);

  const handleEnterRoom = async () => {
    setJoining(true);
    try {
      const res = await cohortService.joinLecture(cohortId, lectureId);
      const attId = res.data?.data?.attendanceId ?? res.data?.attendanceId;
      if (attId) {
        attendanceIdRef.current = attId;
        heartbeatTimerRef.current = setInterval(() => {
          cohortService.heartbeatLecture(cohortId, lectureId, { attendanceId: attId }).catch(() => {});
        }, 25000);
      }
      setJoined(true);
    } catch {
      // Allow entering even if attendance fails
      setJoined(true);
    } finally {
      setJoining(false);
    }
  };

  const handleExitRoom = async () => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (attendanceIdRef.current) {
      cohortService.leaveLecture(cohortId, lectureId, { attendanceId: attendanceIdRef.current }).catch(() => {});
      attendanceIdRef.current = null;
    }
    setJoined(false);
  };

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Guest';

  if (loading) {
    return (
      <div className="min-h-screen bg-forest-950 flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-forest-400" />
      </div>
    );
  }

  if (error || !lecture) {
    return (
      <div className="min-h-screen bg-forest-950 flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-sm">
          <AlertCircle size={40} className="mx-auto text-rust-400" />
          <h3 className="font-serif text-xl text-cream-50">Lecture Room Unavailable</h3>
          <p className="text-sm text-forest-400">{error || 'This lecture room is not available.'}</p>
          <Link
            to={user?.role === 'EXPERT' ? `/expert/cohorts/${cohortId}` : user?.role === 'SUPER_ADMIN' ? `/admin/cohorts/${cohortId}` : '/my-cohort'}
            className="inline-flex items-center gap-2 text-sm text-forest-400 hover:text-cream-50 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Cohort Classroom
          </Link>
        </div>
      </div>
    );
  }

  const roomUrl = lecture.meetingUrl ? `${lecture.meetingUrl}?userName=${encodeURIComponent(userName)}` : null;

  // ── In-Call View ─────────────────────────────────────────────
  if (joined && roomUrl) {
    return (
      <div className="min-h-screen bg-forest-950 flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 bg-forest-900 border-b border-forest-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-sm text-white">TourEnt</span>
              <span className="font-serif font-semibold text-sm text-green-400"> Hub</span>
            </Link>
            <div className="h-4 w-px bg-forest-700" />
            <span className="text-xs font-medium text-forest-300 truncate max-w-xs">{lecture.cohortName || 'Cohort'} · {lecture.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Live Lecture
            </span>
            <span className="text-xs text-forest-400">Signed in as <strong className="text-forest-200">{userName}</strong></span>
            <button
              onClick={handleExitRoom}
              className="text-xs text-rust-400 hover:text-rust-300 ml-2"
            >
              Exit Classroom
            </button>
          </div>
        </div>

        {/* Video Iframe */}
        <div className="flex-1">
          <iframe
            src={roomUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full border-0"
            style={{ minHeight: 'calc(100vh - 53px)' }}
            title={lecture.title}
          />
        </div>
      </div>
    );
  }

  // ── Pre-Join Lobby ───────────────────────────────────────────
  const dateStr = lecture.scheduledAt
    ? new Date(lecture.scheduledAt).toLocaleDateString('en-GH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const timeStr = lecture.scheduledAt
    ? new Date(lecture.scheduledAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className="min-h-screen bg-forest-950 text-cream-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-forest-900/80 border border-forest-800 rounded-2xl p-8 space-y-6 text-center backdrop-blur-md shadow-2xl">
        <div className="w-14 h-14 bg-forest-800 border border-forest-700 rounded-2xl flex items-center justify-center mx-auto text-green-400">
          <Video size={28} />
        </div>

        <div>
          <span className="text-[10px] uppercase font-bold tracking-widest text-green-400 bg-green-950/60 border border-green-800/60 px-3 py-1 rounded-full">
            Live Online Lecture
          </span>
          <h2 className="font-serif text-2xl font-bold text-white mt-3">{lecture.title}</h2>
          <p className="text-xs text-forest-300 mt-1">{lecture.cohortName}</p>
        </div>

        {lecture.description && (
          <p className="text-xs text-forest-300 leading-relaxed bg-forest-950/40 rounded-xl p-3 border border-forest-800/60 text-left">
            {lecture.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 text-xs text-forest-300 bg-forest-950/40 rounded-xl p-3 border border-forest-800/60 text-left">
          <div>
            <span className="text-forest-400 block text-[10px] uppercase">Date & Time</span>
            <p className="font-semibold text-white mt-0.5">{dateStr} · {timeStr}</p>
          </div>
          <div>
            <span className="text-forest-400 block text-[10px] uppercase">Duration</span>
            <p className="font-semibold text-white mt-0.5">{lecture.duration} minutes</p>
          </div>
        </div>

        <div className="space-y-3 pt-2">
          {roomUrl ? (
            <button
              onClick={handleEnterRoom}
              disabled={joining}
              className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-medium text-sm transition-all shadow-lg hover:shadow-green-900/30 flex items-center justify-center gap-2"
            >
              {joining ? <Loader2 size={16} className="animate-spin" /> : null}
              {joining ? 'Entering Room...' : 'Enter Lecture Classroom →'}
            </button>
          ) : (
            <p className="text-xs text-amber-400 bg-amber-950/40 border border-amber-800/40 rounded-xl p-3">
              Meeting room link is being prepared by your instructor. Please refresh in a moment.
            </p>
          )}

          <Link
            to={user?.role === 'EXPERT' ? `/expert/cohorts/${cohortId}` : user?.role === 'SUPER_ADMIN' ? `/admin/cohorts/${cohortId}` : '/my-cohort'}
            className="block text-xs text-forest-400 hover:text-cream-50 transition-colors pt-2"
          >
            ← Return to Cohort
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CohortLectureRoomPage;
